/**
 * IndexedDB-based Offline Database Service
 * Provides robust offline storage for sessions, messages, and generated materials
 * Supports unlimited storage (within device constraints) unlike localStorage
 */

import { ChatSession, Message, GeneratedMaterial, User } from '../types';

const DB_NAME = 'luca_offline_db';
const DB_VERSION = 2;

// Store names
const STORES = {
  SESSIONS: 'sessions',
  MESSAGES: 'messages',
  MATERIALS: 'materials',
  ATTACHMENTS: 'attachments',
  SYNC_QUEUE: 'sync_queue',
  USER_DATA: 'user_data',
  CACHE_METADATA: 'cache_metadata'
} as const;

interface SyncQueueItem {
  id: string;
  type: 'session' | 'message' | 'material' | 'user';
  action: 'create' | 'update' | 'delete';
  data: any;
  timestamp: number;
  retryCount: number;
  priority: number;
}

interface CacheMetadata {
  key: string;
  lastUpdated: number;
  version: number;
  size: number;
}

interface AttachmentCache {
  id: string;
  sessionId: string;
  messageId: string;
  data: string;
  mimeType: string;
  name: string;
  size: number;
  cachedAt: number;
}

class OfflineDatabase {
  private db: IDBDatabase | null = null;
  private dbReady: Promise<void>;
  private isInitialized = false;

  constructor() {
    this.dbReady = this.initDatabase();
  }

  /**
   * Initialize the IndexedDB database
   */
  private async initDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        console.warn('[OfflineDB] IndexedDB not supported, falling back to localStorage');
        resolve();
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        console.error('[OfflineDB] Failed to open database:', (event.target as IDBOpenDBRequest).error);
        reject((event.target as IDBOpenDBRequest).error);
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        this.isInitialized = true;
        console.log('[OfflineDB] Database initialized successfully');
        
        // Handle connection errors
        this.db.onerror = (event) => {
          console.error('[OfflineDB] Database error:', event);
        };

        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;

        console.log(`[OfflineDB] Upgrading database from v${oldVersion} to v${DB_VERSION}`);

        // Sessions store
        if (!db.objectStoreNames.contains(STORES.SESSIONS)) {
          const sessionsStore = db.createObjectStore(STORES.SESSIONS, { keyPath: 'id' });
          sessionsStore.createIndex('userId', 'userId', { unique: false });
          sessionsStore.createIndex('lastModified', 'lastModified', { unique: false });
          sessionsStore.createIndex('createdAt', 'createdAt', { unique: false });
          sessionsStore.createIndex('syncStatus', 'syncStatus', { unique: false });
        }

        // Messages store (for quick message lookup)
        if (!db.objectStoreNames.contains(STORES.MESSAGES)) {
          const messagesStore = db.createObjectStore(STORES.MESSAGES, { keyPath: 'id' });
          messagesStore.createIndex('sessionId', 'sessionId', { unique: false });
          messagesStore.createIndex('timestamp', 'timestamp', { unique: false });
          messagesStore.createIndex('isPinned', 'isPinned', { unique: false });
        }

        // Generated materials store
        if (!db.objectStoreNames.contains(STORES.MATERIALS)) {
          const materialsStore = db.createObjectStore(STORES.MATERIALS, { keyPath: 'id' });
          materialsStore.createIndex('userId', 'userId', { unique: false });
          materialsStore.createIndex('sessionId', 'sessionId', { unique: false });
          materialsStore.createIndex('type', 'type', { unique: false });
          materialsStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Attachments cache store
        if (!db.objectStoreNames.contains(STORES.ATTACHMENTS)) {
          const attachmentsStore = db.createObjectStore(STORES.ATTACHMENTS, { keyPath: 'id' });
          attachmentsStore.createIndex('sessionId', 'sessionId', { unique: false });
          attachmentsStore.createIndex('messageId', 'messageId', { unique: false });
          attachmentsStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        }

        // Sync queue store
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          syncStore.createIndex('priority', 'priority', { unique: false });
        }

        // User data store
        if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
          db.createObjectStore(STORES.USER_DATA, { keyPath: 'id' });
        }

        // Cache metadata store
        if (!db.objectStoreNames.contains(STORES.CACHE_METADATA)) {
          const cacheStore = db.createObjectStore(STORES.CACHE_METADATA, { keyPath: 'key' });
          cacheStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
        }
      };
    });
  }

  /**
   * Ensure database is ready before operations
   */
  private async ensureReady(): Promise<void> {
    await this.dbReady;
  }

  /**
   * Generic method to perform a transaction
   */
  private async transaction<T>(
    storeName: string,
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    await this.ensureReady();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);
      const request = operation(store);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== SESSION OPERATIONS ====================

  /**
   * Save a session to IndexedDB
   */
  async saveSession(session: ChatSession): Promise<void> {
    await this.ensureReady();
    
    if (!this.db) {
      // Fallback to localStorage
      this.saveSessionToLocalStorage(session);
      return;
    }

    const sessionToSave = {
      ...session,
      syncStatus: session.syncStatus || 'offline',
      lastModified: Date.now(),
      version: (session.version || 0) + 1
    };

    await this.transaction(STORES.SESSIONS, 'readwrite', (store) => 
      store.put(sessionToSave)
    );

    // Also save messages separately for quick lookup
    if (session.messages && session.messages.length > 0) {
      const pinnedIds = (session.pinnedMessages || []).map(p => typeof p === 'string' ? p : p.id);
      await this.saveSessionMessages(session.id, session.messages, pinnedIds);
    }

    console.log(`[OfflineDB] Saved session: ${session.id}`);
  }

  /**
   * Save messages for a session
   */
  private async saveSessionMessages(
    sessionId: string, 
    messages: Message[], 
    pinnedMessageIds: string[]
  ): Promise<void> {
    if (!this.db) return;

    const transaction = this.db.transaction(STORES.MESSAGES, 'readwrite');
    const store = transaction.objectStore(STORES.MESSAGES);

    for (const message of messages) {
      const messageToSave = {
        ...message,
        sessionId,
        isPinned: pinnedMessageIds.includes(message.id),
        timestamp: message.timestamp instanceof Date 
          ? message.timestamp.getTime() 
          : message.timestamp
      };
      store.put(messageToSave);
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get a session by ID
   */
  async getSession(sessionId: string): Promise<ChatSession | null> {
    await this.ensureReady();
    
    if (!this.db) {
      return this.getSessionFromLocalStorage(sessionId);
    }

    try {
      const session = await this.transaction<ChatSession | undefined>(
        STORES.SESSIONS, 
        'readonly', 
        (store) => store.get(sessionId)
      );
      return session || null;
    } catch (error) {
      console.error('[OfflineDB] Failed to get session:', error);
      return null;
    }
  }

  /**
   * Get all sessions
   */
  async getAllSessions(): Promise<ChatSession[]> {
    await this.ensureReady();
    
    if (!this.db) {
      return this.getAllSessionsFromLocalStorage();
    }

    try {
      const sessions = await this.transaction<ChatSession[]>(
        STORES.SESSIONS,
        'readonly',
        (store) => store.getAll()
      );
      return sessions.sort((a, b) => (Number(b.lastModified) || 0) - (Number(a.lastModified) || 0));
    } catch (error) {
      console.error('[OfflineDB] Failed to get all sessions:', error);
      return [];
    }
  }

  /**
   * Get sessions for a specific user
   */
  async getUserSessions(userId: string | null): Promise<ChatSession[]> {
    await this.ensureReady();
    
    if (!this.db) {
      const all = this.getAllSessionsFromLocalStorage();
      return all.filter(s => userId ? s.userId === userId : !s.userId);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORES.SESSIONS, 'readonly');
      const store = transaction.objectStore(STORES.SESSIONS);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onsuccess = () => {
        const sessions = request.result.sort((a, b) => b.lastModified - a.lastModified);
        resolve(sessions);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string): Promise<void> {
    await this.ensureReady();
    
    if (!this.db) {
      this.deleteSessionFromLocalStorage(sessionId);
      return;
    }

    // Delete session
    await this.transaction(STORES.SESSIONS, 'readwrite', (store) => 
      store.delete(sessionId)
    );

    // Delete associated messages
    await this.deleteSessionMessages(sessionId);

    // Delete associated attachments
    await this.deleteSessionAttachments(sessionId);

    console.log(`[OfflineDB] Deleted session: ${sessionId}`);
  }

  /**
   * Delete messages for a session
   */
  private async deleteSessionMessages(sessionId: string): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORES.MESSAGES, 'readwrite');
      const store = transaction.objectStore(STORES.MESSAGES);
      const index = store.index('sessionId');
      const request = index.openCursor(IDBKeyRange.only(sessionId));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // ==================== PINNED MESSAGES ====================

  /**
   * Get pinned messages for a session
   */
  async getPinnedMessages(sessionId: string): Promise<Message[]> {
    await this.ensureReady();
    
    if (!this.db) {
      const session = this.getSessionFromLocalStorage(sessionId);
      if (!session) return [];
      const pinnedIds = (session.pinnedMessages || []).map(p => typeof p === 'string' ? p : p.id);
      return session.messages.filter(m => 
        pinnedIds.includes(m.id)
      );
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORES.MESSAGES, 'readonly');
      const store = transaction.objectStore(STORES.MESSAGES);
      const index = store.index('sessionId');
      const request = index.getAll(sessionId);

      request.onsuccess = () => {
        const messages = request.result.filter(m => m.isPinned);
        resolve(messages.sort((a, b) => a.timestamp - b.timestamp));
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== GENERATED MATERIALS ====================

  /**
   * Save a generated material
   */
  async saveMaterial(material: GeneratedMaterial, sessionId?: string): Promise<void> {
    await this.ensureReady();
    
    const materialToSave = {
      ...material,
      sessionId: sessionId || material.id,
      cachedAt: Date.now()
    };

    if (!this.db) {
      // Fallback to localStorage
      const materials = JSON.parse(localStorage.getItem('luca_materials') || '[]');
      const index = materials.findIndex((m: any) => m.id === material.id);
      if (index >= 0) {
        materials[index] = materialToSave;
      } else {
        materials.push(materialToSave);
      }
      localStorage.setItem('luca_materials', JSON.stringify(materials));
      return;
    }

    await this.transaction(STORES.MATERIALS, 'readwrite', (store) => 
      store.put(materialToSave)
    );

    console.log(`[OfflineDB] Saved material: ${material.id}`);
  }

  /**
   * Get a material by ID
   */
  async getMaterial(materialId: string): Promise<GeneratedMaterial | null> {
    await this.ensureReady();
    
    if (!this.db) {
      const materials = JSON.parse(localStorage.getItem('luca_materials') || '[]');
      return materials.find((m: any) => m.id === materialId) || null;
    }

    try {
      const material = await this.transaction<GeneratedMaterial | undefined>(
        STORES.MATERIALS,
        'readonly',
        (store) => store.get(materialId)
      );
      return material || null;
    } catch (error) {
      console.error('[OfflineDB] Failed to get material:', error);
      return null;
    }
  }

  /**
   * Get all materials for a user
   */
  async getUserMaterials(userId: string): Promise<GeneratedMaterial[]> {
    await this.ensureReady();
    
    if (!this.db) {
      const materials = JSON.parse(localStorage.getItem('luca_materials') || '[]');
      return materials.filter((m: any) => m.userId === userId);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORES.MATERIALS, 'readonly');
      const store = transaction.objectStore(STORES.MATERIALS);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onsuccess = () => {
        const materials = request.result.sort((a, b) => b.createdAt - a.createdAt);
        resolve(materials);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get materials for a session
   */
  async getSessionMaterials(sessionId: string): Promise<GeneratedMaterial[]> {
    await this.ensureReady();
    
    if (!this.db) {
      const materials = JSON.parse(localStorage.getItem('luca_materials') || '[]');
      return materials.filter((m: any) => m.sessionId === sessionId);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORES.MATERIALS, 'readonly');
      const store = transaction.objectStore(STORES.MATERIALS);
      const index = store.index('sessionId');
      const request = index.getAll(sessionId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete a material
   */
  async deleteMaterial(materialId: string): Promise<void> {
    await this.ensureReady();
    
    if (!this.db) {
      const materials = JSON.parse(localStorage.getItem('luca_materials') || '[]');
      const filtered = materials.filter((m: any) => m.id !== materialId);
      localStorage.setItem('luca_materials', JSON.stringify(filtered));
      return;
    }

    await this.transaction(STORES.MATERIALS, 'readwrite', (store) => 
      store.delete(materialId)
    );
  }

  // ==================== ATTACHMENTS CACHE ====================

  /**
   * Cache an attachment
   */
  async cacheAttachment(attachment: AttachmentCache): Promise<void> {
    await this.ensureReady();
    
    if (!this.db) return;

    await this.transaction(STORES.ATTACHMENTS, 'readwrite', (store) => 
      store.put({
        ...attachment,
        cachedAt: Date.now()
      })
    );
  }

  /**
   * Get a cached attachment
   */
  async getAttachment(attachmentId: string): Promise<AttachmentCache | null> {
    await this.ensureReady();
    
    if (!this.db) return null;

    try {
      const attachment = await this.transaction<AttachmentCache | undefined>(
        STORES.ATTACHMENTS,
        'readonly',
        (store) => store.get(attachmentId)
      );
      return attachment || null;
    } catch (error) {
      console.error('[OfflineDB] Failed to get attachment:', error);
      return null;
    }
  }

  /**
   * Get all attachments for a session
   */
  async getSessionAttachments(sessionId: string): Promise<AttachmentCache[]> {
    await this.ensureReady();
    
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORES.ATTACHMENTS, 'readonly');
      const store = transaction.objectStore(STORES.ATTACHMENTS);
      const index = store.index('sessionId');
      const request = index.getAll(sessionId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete attachments for a session
   */
  private async deleteSessionAttachments(sessionId: string): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORES.ATTACHMENTS, 'readwrite');
      const store = transaction.objectStore(STORES.ATTACHMENTS);
      const index = store.index('sessionId');
      const request = index.openCursor(IDBKeyRange.only(sessionId));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // ==================== SYNC QUEUE ====================

  /**
   * Add item to sync queue
   */
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'timestamp' | 'retryCount'>): Promise<void> {
    await this.ensureReady();

    const queueItem: SyncQueueItem = {
      ...item,
      id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0
    };
    
    if (!this.db) {
      const queue = JSON.parse(localStorage.getItem('luca_sync_queue') || '[]');
      queue.push(queueItem);
      localStorage.setItem('luca_sync_queue', JSON.stringify(queue));
      return;
    }

    await this.transaction(STORES.SYNC_QUEUE, 'readwrite', (store) => 
      store.add(queueItem)
    );

    console.log(`[OfflineDB] Added to sync queue: ${item.type} - ${item.action}`);
  }

  /**
   * Get all items in sync queue
   */
  async getSyncQueue(): Promise<SyncQueueItem[]> {
    await this.ensureReady();
    
    if (!this.db) {
      return JSON.parse(localStorage.getItem('luca_sync_queue') || '[]');
    }

    try {
      const items = await this.transaction<SyncQueueItem[]>(
        STORES.SYNC_QUEUE,
        'readonly',
        (store) => store.getAll()
      );
      return items.sort((a, b) => b.priority - a.priority || a.timestamp - b.timestamp);
    } catch (error) {
      console.error('[OfflineDB] Failed to get sync queue:', error);
      return [];
    }
  }

  /**
   * Remove item from sync queue
   */
  async removeFromSyncQueue(itemId: string): Promise<void> {
    await this.ensureReady();
    
    if (!this.db) {
      const queue = JSON.parse(localStorage.getItem('luca_sync_queue') || '[]');
      const filtered = queue.filter((item: any) => item.id !== itemId);
      localStorage.setItem('luca_sync_queue', JSON.stringify(filtered));
      return;
    }

    await this.transaction(STORES.SYNC_QUEUE, 'readwrite', (store) => 
      store.delete(itemId)
    );
  }

  /**
   * Clear sync queue
   */
  async clearSyncQueue(): Promise<void> {
    await this.ensureReady();
    
    if (!this.db) {
      localStorage.removeItem('luca_sync_queue');
      return;
    }

    await this.transaction(STORES.SYNC_QUEUE, 'readwrite', (store) => 
      store.clear()
    );
  }

  /**
   * Increment retry count for sync item
   */
  async incrementSyncRetry(itemId: string): Promise<void> {
    await this.ensureReady();
    
    if (!this.db) {
      const queue = JSON.parse(localStorage.getItem('luca_sync_queue') || '[]');
      const updated = queue.map((item: any) => 
        item.id === itemId ? { ...item, retryCount: item.retryCount + 1 } : item
      );
      localStorage.setItem('luca_sync_queue', JSON.stringify(updated));
      return;
    }

    const item = await this.transaction<SyncQueueItem | undefined>(
      STORES.SYNC_QUEUE,
      'readonly',
      (store) => store.get(itemId)
    );

    if (item) {
      await this.transaction(STORES.SYNC_QUEUE, 'readwrite', (store) => 
        store.put({ ...item, retryCount: item.retryCount + 1 })
      );
    }
  }

  // ==================== USER DATA ====================

  /**
   * Save user data for offline access
   */
  async saveUserData(user: User): Promise<void> {
    await this.ensureReady();
    
    if (!this.db) {
      localStorage.setItem('luca_offline_user', JSON.stringify(user));
      return;
    }

    await this.transaction(STORES.USER_DATA, 'readwrite', (store) => 
      store.put({ ...user, cachedAt: Date.now() })
    );
  }

  /**
   * Get cached user data
   */
  async getUserData(userId: string): Promise<User | null> {
    await this.ensureReady();
    
    if (!this.db) {
      const userData = localStorage.getItem('luca_offline_user');
      if (!userData) return null;
      const user = JSON.parse(userData);
      return user.id === userId ? user : null;
    }

    try {
      const user = await this.transaction<User | undefined>(
        STORES.USER_DATA,
        'readonly',
        (store) => store.get(userId)
      );
      return user || null;
    } catch (error) {
      console.error('[OfflineDB] Failed to get user data:', error);
      return null;
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{
    sessionsCount: number;
    messagesCount: number;
    materialsCount: number;
    attachmentsCount: number;
    syncQueueCount: number;
    estimatedSize: string;
  }> {
    await this.ensureReady();

    if (!this.db) {
      return {
        sessionsCount: 0,
        messagesCount: 0,
        materialsCount: 0,
        attachmentsCount: 0,
        syncQueueCount: 0,
        estimatedSize: '0 KB'
      };
    }

    const counts = await Promise.all([
      this.getStoreCount(STORES.SESSIONS),
      this.getStoreCount(STORES.MESSAGES),
      this.getStoreCount(STORES.MATERIALS),
      this.getStoreCount(STORES.ATTACHMENTS),
      this.getStoreCount(STORES.SYNC_QUEUE)
    ]);

    // Estimate storage size
    let estimatedSize = '0 KB';
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const usageMB = (estimate.usage || 0) / (1024 * 1024);
      estimatedSize = usageMB >= 1 
        ? `${usageMB.toFixed(2)} MB` 
        : `${((estimate.usage || 0) / 1024).toFixed(2)} KB`;
    }

    return {
      sessionsCount: counts[0],
      messagesCount: counts[1],
      materialsCount: counts[2],
      attachmentsCount: counts[3],
      syncQueueCount: counts[4],
      estimatedSize
    };
  }

  /**
   * Get count of items in a store
   */
  private async getStoreCount(storeName: string): Promise<number> {
    if (!this.db) return 0;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(0);
    });
  }

  /**
   * Clear old data (older than specified days)
   */
  async clearOldData(days: number = 30): Promise<{ cleared: number }> {
    await this.ensureReady();
    
    const cutoffTime = Date.now() - (days * 24 * 60 * 60 * 1000);
    let cleared = 0;

    if (!this.db) {
      return { cleared: 0 };
    }

    // Clear old attachments
    const attachmentsCleaned = await this.clearOldFromStore(
      STORES.ATTACHMENTS, 
      'cachedAt', 
      cutoffTime
    );
    cleared += attachmentsCleaned;

    console.log(`[OfflineDB] Cleared ${cleared} old items`);
    return { cleared };
  }

  /**
   * Clear old items from a store based on an index
   */
  private async clearOldFromStore(
    storeName: string, 
    indexName: string, 
    cutoffTime: number
  ): Promise<number> {
    if (!this.db) return 0;

    return new Promise((resolve) => {
      let count = 0;
      const transaction = this.db!.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);
      const range = IDBKeyRange.upperBound(cutoffTime);
      const request = index.openCursor(range);

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          cursor.delete();
          count++;
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve(count);
      transaction.onerror = () => resolve(count);
    });
  }

  /**
   * Export all data for backup
   */
  async exportData(): Promise<{
    sessions: ChatSession[];
    materials: GeneratedMaterial[];
    exportedAt: number;
  }> {
    const sessions = await this.getAllSessions();
    const materials = await this.transaction<GeneratedMaterial[]>(
      STORES.MATERIALS,
      'readonly',
      (store) => store.getAll()
    ).catch(() => []);

    return {
      sessions,
      materials,
      exportedAt: Date.now()
    };
  }

  /**
   * Import data from backup
   */
  async importData(data: {
    sessions?: ChatSession[];
    materials?: GeneratedMaterial[];
  }): Promise<{ sessionsImported: number; materialsImported: number }> {
    let sessionsImported = 0;
    let materialsImported = 0;

    if (data.sessions) {
      for (const session of data.sessions) {
        await this.saveSession(session);
        sessionsImported++;
      }
    }

    if (data.materials) {
      for (const material of data.materials) {
        await this.saveMaterial(material);
        materialsImported++;
      }
    }

    return { sessionsImported, materialsImported };
  }

  // ==================== LOCALSTORAGE FALLBACKS ====================

  private saveSessionToLocalStorage(session: ChatSession): void {
    try {
      const sessions = this.getAllSessionsFromLocalStorage();
      const index = sessions.findIndex(s => s.id === session.id);
      if (index >= 0) {
        sessions[index] = session;
      } else {
        sessions.push(session);
      }
      localStorage.setItem('ledger_sessions', JSON.stringify(sessions));
    } catch (error) {
      console.error('[OfflineDB] LocalStorage fallback failed:', error);
    }
  }

  private getSessionFromLocalStorage(sessionId: string): ChatSession | null {
    try {
      const sessions = this.getAllSessionsFromLocalStorage();
      return sessions.find(s => s.id === sessionId) || null;
    } catch {
      return null;
    }
  }

  private getAllSessionsFromLocalStorage(): ChatSession[] {
    try {
      const stored = localStorage.getItem('ledger_sessions');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private deleteSessionFromLocalStorage(sessionId: string): void {
    try {
      const sessions = this.getAllSessionsFromLocalStorage();
      const filtered = sessions.filter(s => s.id !== sessionId);
      localStorage.setItem('ledger_sessions', JSON.stringify(filtered));
    } catch (error) {
      console.error('[OfflineDB] Failed to delete from localStorage:', error);
    }
  }

  /**
   * Check if database is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.isInitialized = false;
    }
  }
}

// Export singleton instance
export const offlineDatabase = new OfflineDatabase();

// Export types
export type { SyncQueueItem, CacheMetadata, AttachmentCache };

